<template>
  <div class="workPermit">
    <p v-if="!hasTabAccess">
      {{ $t('noTabPermission') }}
    </p>
    <template v-else>
      <div
        v-if="!showDetail && items.length"
        class="workPermit__toolbar"
      >
        <div class="workPermit__toolbar-main">
          <div
            v-if="showApplyBtn"
            class="workPermit__toolbar-apply"
          >
            <div class="workPermit_hint">
              <div>{{ $t(`termsAndConditions.${regionName}.workPermit.hintForEdinburghTower`,[ $t('workPermit.types.WORK'), 5 ]) }}</div>
              <div>{{ $t(`termsAndConditions.${regionName}.workPermit.hintForOthers`,[ $t('workPermit.types.WORK'), 3 ]) }}</div>
            </div>
            <HKLButton
              color="orange"
              @click="navigateCreateWorkPermitPage()"
            >
              {{ $t('workPermit.applyWorkPermit') }}
            </HKLButton>
          </div>
          <button
            type="button"
            class="btn btn--darkblue workPermit__reset-filter"
            role="reset-filter-button"
            @click="resetFilters"
          >
            {{ $t('action.resetFilter') }}
          </button>
        </div>
      </div>
      <table
        v-show="!showDetail && items.length"
        class="workPermit__summary"
      >
        <thead>
          <tr>
            <th
              v-for="(header, index) in itemHeaders"
              :key="`${header.name}_${index}`"
              :class="header.class"
            >
              <div
                :class="{ 'workPermit__header-title--sortable': header.attrPath }"
                @click.stop="header.attrPath && sortByColumn(header)"
              >
                {{ header.name }}
                <span
                  v-if="header.attrPath"
                  class="workPermit__header-sort-icon"
                >
                  <i
                    v-if="sortByAttr === header.attrPath"
                    :class="[sortReverse ? 'triangle-down' : 'triangle-up']"
                  />
                </span>
              </div>
            </th>
          </tr>
          <tr class="workPermit__filter-row">
            <th><!-- placeholder --></th>
            <th>
              <SearchFilter
                :inputType="INPUT_TYPE.SELECT"
                type="type"
                trackBy="val"
                multiLabel="label"
                :searchable="false"
                :options="typeFilterOptions"
                :onchange="updateFilterConditions"
                :value="typeFilterDisplayValue"
                :disabled="true"
              />
            </th>
            <th>
              <SearchFilter
                :inputType="INPUT_TYPE.INPUT"
                type="code"
                :onchange="updateFilterConditions"
                :value="getFilterValue('code', '')"
              />
            </th>
            <th>
              <SearchFilter
                :inputType="INPUT_TYPE.INPUT"
                type="description"
                :onchange="updateFilterConditions"
                :value="getFilterValue('description', '')"
              />
            </th>
            <th>
              <SearchFilter
                :inputType="INPUT_TYPE.SELECT"
                type="status"
                trackBy="val"
                multiLabel="label"
                :searchable="false"
                :options="statusFilterOptions"
                :onchange="updateFilterConditions"
                :value="statusFilterDisplayValue"
              />
            </th>
            <th>
              <SearchFilter
                :inputType="INPUT_TYPE.INPUT"
                type="building"
                :onchange="updateFilterConditions"
                :value="buildingFilterDisplayValue"
                :disabled="!isHKLDProject"
              />
            </th>
            <th>
              <SearchFilter
                :inputType="INPUT_TYPE.INPUT"
                type="floor"
                :onchange="updateFilterConditions"
                :value="getFilterValue('floor', '')"
              />
            </th>
            <th>
              <SearchFilter
                :inputType="INPUT_TYPE.INPUT"
                type="contractor"
                :onchange="updateFilterConditions"
                :value="getFilterValue('contractor', '')"
              />
            </th>
            <th class="duration-filter">
              <SearchFilter
                :inputType="INPUT_TYPE.DATE"
                type="duration"
                :disabledDate="false"
                :onchange="updateFilterConditions"
                :value="getFilterValue('duration', ['', ''])"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, index) in sortedFilteredItems">
            <tr
              :key="item.id"
              :class="['workPermit__item', `workPermit__item-${ index % 2 ? 'odd' : 'even' }` ]"
            >
              <td>
                <i
                  v-show="showCollapse(item)"
                  :class="['tree-arrow pointer', item.collapsed ? 'fa-angle-right' : 'fa-angle-down', 'fa angle']"
                  @click="switchArrow(item)"
                />
              </td>
              <td class="bold">
                {{ workPermitTypeLabel(item.type) }}
              </td>
              <td>
                <span
                  :class="{'link_text': !excludeDraft(item)}"
                  @click="!excludeDraft(item) && viewWorkPermit(item)"
                >
                  {{ item.code || emptyValue }}
                </span>
              </td>
              <td>{{ item.workType || emptyValue }}</td>
              <td>{{ formatStatus(item.status) | customStatusLabel }}</td>
              <td>{{ getBuilding(item) || emptyValue }}</td>
              <td>{{ getFloors(item) }}</td>
              <td>{{ (item.contractor || {}).name || emptyValue }}</td>
              <td>{{ getDuration(item) }}</td>
            </tr>
            <tr
              v-for="hoisting in item.hoistings"
              v-show="!item.collapsed"
              :key="hoisting.id"
              :class="['workPermit__item', `workPermit__item-${ index % 2 ? 'odd' : 'even' }` ]"
            >
              <td><!-- placeholder --></td>
              <td>
                <span>{{ workPermitTypeLabel(hoisting.type) }}</span>
                <img
                  v-if="hotWorkRequired(hoisting)"
                  src="/ic_hotwork.png"
                  alt="hotwork"
                >
              </td>
              <td>
                <span
                  :class="{'link_text': !excludeDraft(item)}"
                  @click="!excludeDraft(item) && viewWorkPermit(item, hoisting.id)"
                >
                  {{ hoisting.code || emptyValue }}
                </span>
              </td>
              <td>{{ hoisting.description || emptyValue }}</td>
              <td>{{ formatStatus(hoisting.status) | customStatusLabel }}</td>
              <td>{{ getBuilding(hoisting) || emptyValue }}</td>
              <td>{{ getFloors(hoisting) }}</td>
              <td>{{ (hoisting.contractor || {}).name || emptyValue }}</td>
              <td>{{ getDuration(hoisting) }}</td>
            </tr>
          </template>
        </tbody>
      </table>

      <div
        v-show="!(showDetail || items.length)"
        class="workPermit_empty"
      >
        <div v-if="isMainUser || isSubUser || isCurrentVendorAccount">
          <p v-html="$t(`termsAndConditions.${regionName}.workPermit.hintForTenantApplyWorkPermit`)" />
        </div>
        <p
          v-else
          class="bold"
        >
          {{ $t('workPermit.youHaveNoWorkPermit') }}
        </p>
        <template v-if="showFirstApplyBtn">
          <HKLButton
            :color="enableFirstApplyButton ? 'orange' : 'disabledgrey'"
            @click="nextAction()"
          >
            {{ $t('workPermit.applyWorkPermit') }}
          </HKLButton>
          <MainApplyModal
            v-if="!enableFirstApplyButton && isMainUser"
            ref="modal"
          />
          <SubApplyModal
            v-if="!enableFirstApplyButton && !isMainUser && isSubUser"
            ref="modal"
          />
        </template>
      </div>

      <div v-show="showDetail">
        <div class="back">
          <i @click="_backToSummary">&lt; {{ $t('workPermit.backToList') }}</i>
        </div>
        <WorkPermitDetail
          ref="workPermitDetail"
          :focusPermitId="focusPermitId"
          :isPPSuspened="isPPSuspened"
          :projectPermit="projectPermit"
          :isProjectAvailable="isProjectAvailable"
        />
      </div>
    </template>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { getCurrentInstance, ref, unref } from '@vue/composition-api'
import EventBus from '@/utils/eventBus'
import navigation from '@/utils/navigation'
import mutationTypes from '@/store/mutation-types'
import * as workPermitApi from '@/api/workPermit/request'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { checkIfHKLProjectRelatedRoute } from '@/router/utils'
import { getBuildingFloorZoneMap } from '@/api/buildings/request'
import { getBuilding, navigate } from '@/api/workPermit/util'
import { UserDomain } from '@/utils/consts'
import { useStoreGetters } from '@/store'
import SearchFilter from '@/modules/contacts/SearchFilter'
import { INPUT_TYPE } from '@/modules/contacts/searchFilter.const'
import { orderByPermitCode, permitCodeSortKey } from '@/utils/sortingHelper'
import HKLButton from '../../controls/Button/HKLButton'
import { getDuration, getIsProjectAvailable, maskStatusForExternalUser, disableTenantContractorEdit } from './utils'
import { WORK_PERMIT_STATUS, WORK_PERMIT_TYPES } from './consts'
import WorkPermitDetail from './WorkPermitDetail'
import SubApplyModal from './Modals/SubApplyModal'
import MainApplyModal from './Modals/MainApplyModal'
import { getWorkPermitTypeLabel } from './regionalWorkPermitLabels'

const STATUS_FILTER_VALUES_HKL = [
  WORK_PERMIT_STATUS.DRAFT,
  WORK_PERMIT_STATUS.WAPPR,
  WORK_PERMIT_STATUS.ACKNOWLEDGED,
  WORK_PERMIT_STATUS.REVIEWED,
  WORK_PERMIT_STATUS.REVIEWED2,
  WORK_PERMIT_STATUS.APPR,
  WORK_PERMIT_STATUS.REJECTED,
  WORK_PERMIT_STATUS.ENDED,
  WORK_PERMIT_STATUS.SUSPENDED,
  WORK_PERMIT_STATUS.CANCELLED,
  WORK_PERMIT_STATUS.INPROGRESS,
  WORK_PERMIT_STATUS.COMPLETED,
  WORK_PERMIT_STATUS.EXPIRED,
]

const STATUS_FILTER_VALUES_EXTERNAL = [
  WORK_PERMIT_STATUS.DRAFT,
  WORK_PERMIT_STATUS.WAPPR,
  WORK_PERMIT_STATUS.APPR,
  WORK_PERMIT_STATUS.REJECTED,
  WORK_PERMIT_STATUS.ENDED,
  WORK_PERMIT_STATUS.SUSPENDED,
  WORK_PERMIT_STATUS.CANCELLED,
  WORK_PERMIT_STATUS.INPROGRESS,
  WORK_PERMIT_STATUS.COMPLETED,
  WORK_PERMIT_STATUS.EXPIRED,
]

export default {
  name: 'WorkPermitView',
  components: {
    WorkPermitDetail,
    HKLButton,
    MainApplyModal,
    SubApplyModal,
    SearchFilter,
  },
  props: {
    permitId: {
      type: String,
      default: undefined,
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const { regionName } = useStoreGetters('regions', 'regionName')
    const modal = ref(null)

    const nextAction = () => {
      if (proxy.enableFirstApplyButton) {
        return proxy.navigateCreateWorkPermitPage()
      }
      if (proxy.isMainUser) {
        const [hasMainConAcceptedTnc, hasTenantAcceptedTnc] = [
          lodash.get(proxy.currentTenantProject, 'tncAcceptance.mainContractor'),
          lodash.get(proxy.currentTenantProject, 'tncAcceptance.tenantUser'),
        ]
        if (!hasMainConAcceptedTnc || !hasTenantAcceptedTnc) {
          return unref(modal).open()
        }
      }
      if (!proxy.isMainUser && proxy.isSubUser) {
        if (!proxy.hasActiveProjectPermit) {
          return unref(modal).open()
        }
      }
    }
    return {
      regionName,
      nextAction,
      modal,
    }
  },
  data () {
    return {
      emptyValue: '--',
      showDetail: false,
      focusPermitId: this.permitId,
      projectPermit: '',
      ppId: null,
      INPUT_TYPE,
      filterConditions: '',
      getDuration,
      sites: {},
      sortByAttr: null,
      sortReverse: false,
    }
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantProject']),
    ...mapGetters('projects', ['currentProject', 'purchase']),
    ...mapGetters('user', ['isEngineer', 'isTenant', 'isContractor', 'isContractorAdmin', 'currentUser', 'currentUserContact']),
    ...mapGetters('workPermit', ['buildingFloorZoneMap', 'currentWorkPermit', 'isUnderBatch', 'reviewQueue', 'workPermitsInProject']),
    ...mapGetters('projectContractors', ['isCurrentVendorAccount', 'isMainUser', 'isSubUser', 'currentMainContractor', 'myContractor']),
    itemHeaders () {
      return [
        {
          attrPath: '',
          name: '',
          class: '',
        },
        {
          attrPath: 'type',
          name: this.$t('workPermit.type'),
          class: 'type',
        },
        {
          attrPath: 'permitNo',
          name: this.$t('workPermit.number'),
          class: 'permitNo',
        },
        {
          attrPath: 'workType',
          name: this.$t('workPermit.description'),
          class: 'description',
        },
        {
          attrPath: 'status',
          name: this.$t('status.name'),
          class: 'status',
        },
        {
          attrPath: 'building',
          name: this.$t('workPermit.building'),
          class: 'building',
        },
        {
          attrPath: 'floor',
          name: this.$t('workPermit.floor'),
          class: 'floor',
        },
        {
          attrPath: 'contractor',
          name: this.$t('workPermit.contractor'),
          class: 'contractor',
        },
        {
          attrPath: 'duration',
          name: this.$t('workPermit.duration'),
          class: 'duration',
        },
      ]
    },
    items () {
      return this.workPermitsInProject.map(item => {
        return {
          ...item.self,
          hoistings: orderByPermitCode(item.hoistings),
          collapsed: false,
          isOwn: (item.self.contractorId === this.myContractor.companyId && item.self.projectId === this.myContractor.projectId) ||
          (this.isCurrentVendorAccount && item.self.contractorId === this.currentUser.externalId),
        }
      })
    },
    typeFilterOptions () {
      const types = this.items.flatMap(item => [item.type, ...item.hoistings.map(hoisting => hoisting.type)])
      return [{ label: this.$t('all'), val: '' }].concat(
        [...new Set(types.filter(Boolean))].map(type => ({
          label: getWorkPermitTypeLabel(type),
          val: type,
        })),
      )
    },
    statusFilterOptions () {
      const values = this.isEngineer ? STATUS_FILTER_VALUES_HKL : STATUS_FILTER_VALUES_EXTERNAL
      return [{ label: this.$t('all'), val: '' }].concat(
        values.map(status => ({
          label: this.$options.filters.customStatusLabel(this.formatStatus(status)),
          val: status,
        })),
      )
    },
    typeFilterDisplayValue () {
      return this.typeFilterOptions[0] || { label: this.$t('all'), val: '' }
    },
    nonHKLProjectBuildingDisplay () {
      const project = this.isTenant ? this.currentTenantProject : this.currentProject
      if (this.items.length) {
        return this.getBuilding(this.items[0]) || ''
      }
      const direct = lodash.get(project, 'building')
      if (direct) {
        return this.isContractor ? (direct.fullName || direct.code || '') : (direct.code || '')
      }
      const first = lodash.get(project, 'buildings[0]')
      if (first) {
        return this.isContractor ? (first.fullName || first.code || '') : (first.code || '')
      }
      return ''
    },
    buildingFilterDisplayValue () {
      if (this.isHKLDProject) {
        return this.getFilterValue('building', '')
      }
      return this.nonHKLProjectBuildingDisplay
    },
    statusFilterDisplayValue () {
      const fc = this.filterConditions
      if (!fc || typeof fc !== 'object' || !Object.prototype.hasOwnProperty.call(fc, 'status')) {
        return null
      }
      const raw = lodash.get(fc, 'status.value')
      if (raw === undefined || raw === null) return null
      return this.statusFilterOptions.find(option => option.val === raw) || null
    },
    filteredItems () {
      const matchesText = (value, keyword) => {
        if (!keyword) return true
        const source = String(value || '').toLowerCase()
        return source.includes(keyword.toLowerCase())
      }
      const typeFilter = ''
      const codeFilter = this.getConditionValue('code')
      const descriptionFilter = this.getConditionValue('description')
      const statusFilter = this.getConditionValue('status')
      const buildingFilter = this.isHKLDProject ? this.getConditionValue('building') : ''
      const floorFilter = this.getConditionValue('floor')
      const contractorFilter = this.getConditionValue('contractor')
      const durationRange = this.getConditionValue('duration', ['', ''])
      const parseDate = (date) => {
        if (!date) return null
        const parsed = new Date(date).getTime()
        return Number.isNaN(parsed) ? null : parsed
      }
      const fromTimestamp = durationRange && durationRange[0] ? parseDate(durationRange[0]) : null
      const toTimestamp = durationRange && durationRange[1] ? parseDate(durationRange[1]) : null
      const toTimestampEndOfDay = toTimestamp == null ? null : (toTimestamp + 24 * 60 * 60 * 1000 - 1)
      const permitDescription = (permit) => permit.type === WORK_PERMIT_TYPES.HOISTING ? permit.description : permit.workType
      const matchesPermit = (permit) => {
        const building = this.getBuilding(permit)
        const permitStartDate = parseDate(permit.startDate)
        const permitEndDate = parseDate(permit.endDate || permit.startDate)
        const hasDurationStart = fromTimestamp == null || (permitEndDate != null && permitEndDate >= fromTimestamp)
        const hasDurationEnd = toTimestampEndOfDay == null || (permitStartDate != null && permitStartDate <= toTimestampEndOfDay)
        return (!typeFilter || permit.type === typeFilter) &&
          matchesText(permit.code, codeFilter) &&
          matchesText(permitDescription(permit), descriptionFilter) &&
          (!statusFilter || permit.status === statusFilter) &&
          matchesText(building, buildingFilter) &&
          matchesText((permit.floors || []).join(', '), floorFilter) &&
          matchesText((permit.contractor || {}).name, contractorFilter) &&
          hasDurationStart &&
          hasDurationEnd
      }
      return this.items.reduce((result, item) => {
        const matchedHoistings = item.hoistings.filter(hoisting => matchesPermit(hoisting))
        const hasMatchedChildren = matchedHoistings.length > 0
        const matchedParent = matchesPermit(item)
        if (!matchedParent && !hasMatchedChildren) return result
        result.push({
          ...item,
          // Always expand when matched children exist, so filtered child rows stay visible.
          collapsed: hasMatchedChildren ? false : item.collapsed,
          hoistings: matchedHoistings,
        })
        return result
      }, [])
    },
    sortedFilteredItems () {
      const list = this.filteredItems
      if (!this.sortByAttr || !list.length) return list
      const order = this.sortReverse ? 'desc' : 'asc'
      const sortValue = (permit) => this.getPermitSortValue(permit, this.sortByAttr)
      return lodash.orderBy(list, [sortValue], [order]).map(item => ({
        ...item,
        hoistings: lodash.orderBy(item.hoistings || [], [sortValue], [order]),
      }))
    },
    isHKLDProject () {
      return checkIfHKLProjectRelatedRoute()
    },
    isProjectAvailable () {
      const project = this.isHKLDProject ? this.currentProject : this.currentTenantProject
      const projectAvailable = getIsProjectAvailable({
        isTenantProject: !this.isHKLDProject,
        project,
        isEngineer: this.isEngineer,
      })
      return projectAvailable
    },
    isContractorInvited () {
      return lodash.get(this.myContractor, 'primaryContact.active')
    },
    isTenantMainUser () {
      return this.isMainUser && this.isTenant
    },
    isVendorMainUser () {
      return this.isMainUser && this.currentUser.domain === UserDomain.CMS
    },
    showApplyBtnForMainUser () {
      return this.isVendorMainUser || this.isTenantMainUserWithPPAllCancelled
    },
    showApplyBtnForSubUser () {
      return this.isSubUser && (!this.isTenant || this.hasActiveProjectPermit) && !disableTenantContractorEdit()
    },
    showApplyBtn () {
      return this.isProjectAvailable && (this.showApplyBtnForContractor || this.isCurrentVendorAccount)
    },
    showApplyBtnForContractor () {
      return this.isContractorInvited &&
        (this.showApplyBtnForMainUser || this.showApplyBtnForSubUser)
    },
    currentProjectIsNotDraft () {
      return this.isTenant ? this.currentTenantProject.status !== 'DRAFT' : this.currentProject.status !== 'DRAFT'
    },
    showFirstApplyBtn () {
      return this.isProjectAvailable && this.currentProjectIsNotDraft && (this.isCurrentVendorAccount || this.showFirstApplyBtnForContractor)
    },
    showFirstApplyBtnForContractor () {
      return this.isContractorInvited && (this.isMainUser || this.isSubUser)
    },
    hasActiveProjectPermit () {
      const status = lodash.get(this.projectPermit, 'status')
      return status ? [WORK_PERMIT_STATUS.APPR, WORK_PERMIT_STATUS.SUSPENDED].includes(status) : false
    },
    hasTabAccess () {
      const isProjectPIC = this.currentUser.id === lodash.get(this.currentProject, 'manager.id')
      const isProjectRep = lodash.get(this.currentProject, 'accounts', []).some(account => {
        const { ProjectAccount } = account
        return ProjectAccount.userId === this.currentUser.id && ProjectAccount.invitedAt
      })
      if (!this.isEngineer || !this.isHKLDProject || isProjectPIC || isProjectRep) {
        return true
      }

      const required = this.currentProject.buildings && this.currentProject.buildings.length > 0
      const buildings = required
        ? this.currentProject.buildings
        : (this.sites[this.purchase.siteId] || [])
      const buildingAccess = buildings.some(building =>
        (this.currentUser.buildings || []).some(item => item.code === building.code || item.code === building))
      const workPermitAccess = aclHelper.getAccess(tmsPermsTypes.workpermit.read)
      return buildingAccess && workPermitAccess
    },
    enableFirstApplyButton () {
      return !this.isTenant || (
        this.isMainUser &&
        lodash.get(this.currentTenantProject, 'tncAcceptance.mainContractor') &&
        lodash.get(this.currentTenantProject, 'tncAcceptance.tenantUser')
      ) || (
        this.isSubUser &&
        this.hasActiveProjectPermit
      )
    },
    isPPSuspened () {
      return lodash.get(this.projectPermit, 'status') === WORK_PERMIT_STATUS.SUSPENDED || lodash.get(this.currentWorkPermit, 'status') === WORK_PERMIT_STATUS.SUSPENDED
    },
    PPAllCancelled () {
      return this.workPermitsInProject.filter(wp => {
        return wp.self.type === WORK_PERMIT_TYPES.PROJECT
      }).every(wp => wp.self.status === WORK_PERMIT_STATUS.CANCELLED)
    },
    isTenantMainUserFirstApply () {
      return !this.items.length && this.isTenantMainUser
    },
    isTenantMainUserWithPPAllCancelled () {
      return this.isTenantMainUser && this.PPAllCancelled
    },
    isTenantMainUserAbleToCreateNewPP () {
      return this.isTenantMainUserFirstApply || this.isTenantMainUserWithPPAllCancelled
    },
    projectId () {
      return this.$route.params.id
    },
  },
  watch: {
    async isSubUser () {
      await this.getProjectPermit()
    },
    async $route () {
      this.showDetail = this.$route.query.showDetail
      const focusPermitId = this.$route.query.hoistingId || this.$route.query.wpId
      if (this.$route.query.wpId) {
        this.focusPermitId = focusPermitId
        await this.getWorkPermitsInProject()
        this.setCurrentWorkPermitByWpIdFromProject(this.$route.query.wpId)
        this.scheduleJumpToCurrentWorkPermit()
      } else {
        this.focusPermitId = null
        this.setCurrentWorkPermit({})
      }
    },
    async currentWorkPermit () {
      this.showDetail = this.$route.query.showDetail
      await this.syncUpProjectPermit()
    },
  },
  async mounted () {
    const hoistingId = this.$route.query.hoistingId
    const showDetail = this.$route.query.showDetail
    if (hoistingId) {
      this.focusPermitId = hoistingId
    }
    if (!this.focusPermitId) {
      this.focusPermitId = this.$route.query.wpId
    }
    if (showDetail && !lodash.isEmpty(this.currentWorkPermit)) {
      this.showDetail = showDetail
    }
    await this.getWorkPermitsInProject()
    if (this.$route.query.wpId) {
      this.setCurrentWorkPermitByWpIdFromProject(this.$route.query.wpId)
    }
    await this.getProjectPermit()
    await this.setUpBuildingFloorZone()
    this.jumpToCurrentWorkPermit()
  },
  methods: {
    ...mapActions('workPermit', ['advanceReviewQueueAndNavigate', 'createWorkPermit', 'getWorkPermitById', 'getWorkPermitsInProjectByProjectId']),
    ...mapMutations('workPermit', {
      setCurrentWorkPermit: mutationTypes.WORKPERMIT_SET_CURRENT_WORKPERMIT,
      setBuildingFloorMap: mutationTypes.WORKPERMIT_SET_BUILDING_FLOOR_MAP,
    }),
    showCollapse (item = {}) {
      return lodash.get(item, 'hoistings.length')
    },
    hotWorkRequired (item) {
      return item.type === WORK_PERMIT_TYPES.HOISTING && item.hotWork
    },
    switchArrow (item) {
      item.collapsed = !item.collapsed
      this.$forceUpdate()
    },
    getBuilding,
    getFloors (item) {
      if (item.isAllFloors) {
        return this.$t('workPermit.allFloors')
      } else {
        return lodash.get(item, 'floors.length') ? item.floors.join(', ') : this.emptyValue
      }
    },
    updateFilterConditions (condition) {
      this.filterConditions = Object.assign({}, this.filterConditions, condition)
    },
    getFilterValue (name, fallback = '') {
      return lodash.get(this.filterConditions, `${name}.value`, fallback)
    },
    getConditionValue (name, fallback = '') {
      return this.getFilterValue(name, fallback)
    },
    resetFilters () {
      this.filterConditions = ''
      this.sortByAttr = null
      this.sortReverse = false
    },
    sortByColumn (header) {
      const path = header && header.attrPath
      if (!path) return
      if (this.sortByAttr === path) {
        this.sortReverse = !this.sortReverse
      } else {
        this.sortByAttr = path
        this.sortReverse = false
      }
    },
    getPermitSortValue (permit, attrPath) {
      switch (attrPath) {
        case 'type':
          return (getWorkPermitTypeLabel(permit.type) || '').toLowerCase()
        case 'permitNo':
          return permitCodeSortKey(permit.code)
        case 'workType': {
          const desc = permit.type === WORK_PERMIT_TYPES.HOISTING ? permit.description : permit.workType
          return (desc || '').toLowerCase()
        }
        case 'status': {
          const st = this.formatStatus(permit.status)
          const label = this.$options.filters.customStatusLabel(st) || ''
          return String(label).toLowerCase()
        }
        case 'building':
          return (this.getBuilding(permit) || '').toLowerCase()
        case 'floor':
          return (permit.isAllFloors
            ? this.$t('workPermit.allFloors')
            : ((permit.floors || []).join(', ') || this.emptyValue)
          ).toLowerCase()
        case 'contractor':
          return ((permit.contractor || {}).name || '').toLowerCase()
        case 'duration': {
          const t = permit.startDate ? new Date(permit.startDate).getTime() : 0
          return Number.isNaN(t) ? 0 : t
        }
        default:
          return ''
      }
    },
    gotoCreatePage (item) {
      let name = 'tenantCreateWorkPermit'
      let from = 'tenantProjectDetail'
      if (!item.isTenantProject) {
        name = 'vendorCreateWorkPermit'
        from = 'vendorProjectDetail'
      }
      navigation.toPage(
        name,
        { id: item.id },
        { from },
      )
    },
    formatStatus (status) {
      return this.isEngineer ? status : maskStatusForExternalUser(status)
    },
    workPermitTypeLabel (type) {
      return getWorkPermitTypeLabel(type)
    },
    async navigateCreateWorkPermitPage () {
      const contractorId = this.myContractor.companyId || this.currentUser.externalId
      const projectId = this.isTenant ? this.currentTenantProject.id : this.currentProject.id
      const type = this.isTenantMainUserAbleToCreateNewPP ? WORK_PERMIT_TYPES.PROJECT : WORK_PERMIT_TYPES.WORK
      const workpermit = await this.createWorkPermit({
        projectId,
        contractorId,
        isTenantProject: this.isTenant,
        type,
        parentId: (this.isTenant && type === WORK_PERMIT_TYPES.WORK) ? this.ppId : null,
      })
      if (workpermit) {
        this.gotoCreatePage(workpermit)
      }
    },
    excludeDraft (wp) {
      return !wp.isOwn && wp.status === WORK_PERMIT_STATUS.DRAFT
    },
    viewWorkPermit (wp, hoistingId) {
      if (wp.status !== WORK_PERMIT_STATUS.DRAFT) {
        this.showDetail = true
      }
      this.focusPermitId = hoistingId || wp.id
      navigate(wp, hoistingId)
    },
    _backToSummary () {
      let query = Object.assign({}, this.$route.query)
      delete query.showDetail
      delete query.hoistingId
      delete query.wpId
      delete query.underBatch
      this.$router.replace({ query })
    },
    async getProjectPermit () {
      // No Project Permit for HKLD/CMS project
      if (this.isHKLDProject) return

      const projectPermits = await workPermitApi.getTPProjectPermit(this.projectId) || []
      this.projectPermit = projectPermits.find(wp => wp.status !== WORK_PERMIT_STATUS.CANCELLED) || {}
      this.ppId = lodash.get(this.projectPermit, 'id')
    },
    setCurrentWorkPermitByWpIdFromProject (wpId) {
      this.workPermitsInProject.map(workPermit => {
        if (workPermit.self.id === wpId) {
          const currentWP = lodash.cloneDeep(workPermit)
          currentWP.self.hoistings = currentWP.hoistings
          this.setCurrentWorkPermit(currentWP.self)
        }
      })
    },
    getDisplayedWorkPermitSequence () {
      const wpId = this.$route.query.wpId
      if (!wpId) return []
      const projectWorkPermit = this.workPermitsInProject.find(workPermit => {
        return lodash.get(workPermit, 'self.id') === wpId
      })
      if (!projectWorkPermit || lodash.isEmpty(projectWorkPermit.self)) return []
      return [
        projectWorkPermit.self,
        ...orderByPermitCode(projectWorkPermit.hoistings || []),
      ].filter(permit => {
        return !(permit.status === WORK_PERMIT_STATUS.DRAFT && this.isEngineer)
      })
    },
    getAdjacentDisplayedWorkPermit (offset) {
      const currentId = this.$route.query.hoistingId || this.$route.query.wpId
      const sequence = this.getDisplayedWorkPermitSequence()
      const index = sequence.findIndex(permit => permit.id === currentId)
      if (index < 0) return null
      const permit = sequence[index + offset] || null
      if (
        this.isUnderBatch &&
        offset < 0 &&
        this.$route.query.hoistingId &&
        permit &&
        permit.id === this.$route.query.wpId
      ) {
        return null
      }
      return permit
    },
    navigateDisplayedWorkPermit (permit) {
      const hoistingId = permit.type === WORK_PERMIT_TYPES.HOISTING ? permit.id : undefined
      if (permit.status !== WORK_PERMIT_STATUS.DRAFT) {
        this.showDetail = true
      }
      this.focusPermitId = hoistingId || permit.id
      navigate(permit, hoistingId, { fromViewAll: false, underBatch: true })
    },
    jumpToCurrentWorkPermit () {
      const focusElement = this.focusPermitId && document.getElementById(this.focusPermitId)
      if (this.showDetail && focusElement) {
        window.scrollTo(0, focusElement.offsetTop + 10)
        EventBus.emit('window_scroll')
        return
      }
      if (this.showDetail &&
        this.focusPermitId === this.currentWorkPermit.id &&
        this.$refs.workPermitDetail &&
        this.$refs.workPermitDetail.$el
      ) {
        window.scrollTo(0, this.$refs.workPermitDetail.$el.offsetTop - 20)
        EventBus.emit('window_scroll')
      }
    },
    scheduleJumpToCurrentWorkPermit () {
      this.$nextTick(() => {
        this.jumpToCurrentWorkPermit()
      })
    },
    async toPreviousWorkPermit () {
      if (!this.isUnderBatch) return false
      const permit = this.getAdjacentDisplayedWorkPermit(-1)
      if (!permit) return false
      this.navigateDisplayedWorkPermit(permit)
      return true
    },
    async advanceReviewQueueToNextWorkPermit () {
      if (!this.isUnderBatch || !(this.reviewQueue || []).length) return false
      const currentId = this.$route.query.hoistingId || this.$route.query.wpId
      let remaining = this.reviewQueue.length
      while (remaining > 0) {
        const permit = await this.advanceReviewQueueAndNavigate()
        if (!permit) return false
        if (permit.id !== currentId) {
          this.focusPermitId = permit.id
          return true
        }
        remaining = (this.reviewQueue || []).length
      }
      return false
    },
    async toNextWorkPermit () {
      if (!this.isUnderBatch) return false
      const displayedPermit = this.getAdjacentDisplayedWorkPermit(1)
      if (displayedPermit) {
        this.navigateDisplayedWorkPermit(displayedPermit)
        return true
      }
      return false
    },
    async syncUpProjectPermit () {
      if (this.currentWorkPermit && this.projectPermit &&
        this.currentWorkPermit.id === this.projectPermit.id &&
        this.currentWorkPermit.updatedAt !== this.projectPermit.updatedAt) {
        await this.getProjectPermit()
      }
    },
    async getWorkPermitsInProject () {
      await this.getWorkPermitsInProjectByProjectId({
        projectId: this.projectId,
        changeCurrentWorkPermitId: this.$route.query.wpId,
      })
    },
    async setUpBuildingFloorZone () {
      const buildingsInfo = await getBuildingFloorZoneMap()
      this.sites = buildingsInfo.Sites
      this.setBuildingFloorMap(buildingsInfo.BuildingFloorZoneMap)
    },
  },
}
</script>

<style lang="scss" scoped src="styles/table.scss"></style>
<style lang='scss' scoped>
.workPermit {
  &__item {
    &-odd {
      background: #fbfbfb!important;
    }
    &-even {
      background: #ffffff!important;
    }
  }
  .workPermit_hint {
    margin-right: 0;
    color:#E49405;
    text-align: left;
    flex: 0 1 auto;
    min-width: 0;

    > div {
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }
  .workPermit__toolbar {
    width: 100%;
    padding-bottom: 20px;
    box-sizing: border-box;
  }
  .workPermit__toolbar-main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 12px 16px;
    width: 100%;
    min-width: 0;
  }
  .workPermit__toolbar-apply {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    flex: 0 1 auto;
    min-width: 0;
  }
  .workPermit__toolbar .workPermit__reset-filter {
    white-space: nowrap;
    flex-shrink: 0;
  }
  .back {
    padding-bottom: 14px;
    i {
      cursor: pointer;
    }
  }
  .workPermit__header-title--sortable {
    cursor: pointer;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .workPermit__header-sort-icon {
    display: inline-flex;
    min-width: 12px;
    vertical-align: middle;
  }
  .workPermit__summary {
    .workPermit__filter-row th {
      padding-top: 6px;
      padding-bottom: 8px;
      .search-filter-input {
        width: 100%;
      }
      &.duration-filter {
        min-width: 220px;
      }
      &.status ::v-deep .multiselect__single {
        height: auto;
        min-height: 38px;
        line-height: 1.25;
        white-space: normal;
        overflow: visible;
      }
    }
    td:first-child, th:first-child {
      width: 60px;
    }
    td, th {
      word-break: break-word;
      word-wrap: break-word;
      padding: 10px;
      .link_text {
        color: #1779ba;
        text-decoration: underline;
      }
      .link_text:hover {
        cursor: pointer;
      }
    }
  }
  .workPermit_empty {
    width: 80%;
    font-size: 16px;
    margin: 0 auto;
    text-align: center;
    p {
      line-height: 1.81;
      text-align: center;
    }
    button {
      margin-top: 30px;
      padding: 10px 50px;
      height: 42px;
    }
  }
  .bold {
    font-weight: bold;
  }
  .gray_font {
    font-size: 12px;
    color: #878787;
    display: inline-block;
    height: 20px;
    margin-bottom: 5px;
  }
  .workPermit_collapse {
    display: block;
    width: 100%;
    .generalInfo {
      padding: 20px;
    }
    margin-bottom: 20px;
  }

  .tree-arrow {
    font-size: 25px;
    color: #aeaeae;
    position: relative;
    left: 17px;
  }
  .type {
    width: 8%;
  }
  .permitNo {
    width: 11%;
  }
  .description {
    width: 13%;
  }
  .status {
    width: 14%;
    min-width: 168px;
  }
  .building {
    width: 10%
  }
  .floor {
    width: 7%;
  }
  .contractor {
    width: 12%;
    min-width: 0;
  }
}
</style>
